/**
 * `fetchIdentity` — vraagt aan de host-portal of de huidige bezoeker
 * ingelogd is, en zo ja, met welk JWT.
 *
 * ## Contract met de host-portal
 *
 *   - **Endpoint**: GET `<identityUrl>` (volledige absolute URL — verschilt
 *     per Embrace-omgeving). Voorbeeld:
 *     `https://onlycustomers.umbraco13.stable-alpha.embracecloud.io/umbraco/surface/chat/identity`
 *   - **Auth**: cookies/SSO van de host-portal worden meegestuurd via
 *     `credentials: 'include'`. De portal verwacht een logged-in cookie
 *     én bepaalt zelf of die geldig is.
 *   - **Success-response**: `{ "JWT": "eyJhb..." }` (PascalCase, vanuit
 *     ASP.NET-default). Soms `{ "jwt": "..." }` afhankelijk van portal-
 *     versie — accepteer beide.
 *   - **Geen JWT**: alle andere uitkomsten — 401, 5xx, redirect naar
 *     login-pagina, parse-error, timeout, network-failure. Allemaal "deze
 *     bezoeker is niet ingelogd". Nooit gooien.
 *
 * ## Waarom redirects niet volgen
 *
 * Een uitgelogde bezoeker krijgt typisch een 302 naar `/umbraco/login`. Als
 * `fetch` die volgt komt er een HTML-loginpagina terug die we ten onrechte
 * als "iets bruikbaars" zouden kunnen interpreteren. `redirect: 'manual'`
 * geeft een opaque-redirect-response (status 0) waar we niets uit kunnen
 * lezen — exact wat we willen, want het valt door naar het null-pad.
 *
 * ## Foutpad-discipline
 *
 * Niet-ingelogd-zijn is **normale state**, geen incident. Daarom:
 *   - geen `console.error` op 401 / network-fouten (zou logs vervuilen)
 *   - wel een `console.warn` op 5xx (portal-misconfig is wél een signaal)
 *   - nooit een exception naar buiten — caller wil enkel weten "JWT of niks"
 */

/**
 * Resultaat van een identity-check. `jwt: null` representeert élk
 * non-success-pad (uitgelogd, fout, redirect, timeout). Caller hoeft
 * onderscheid niet te maken.
 */
export type IdentityResult = { jwt: string | null };

/**
 * Default time-budget voor de identity-call. 5s is ruim voor een snelle
 * portal-call én laag genoeg om een trage host-portal niet de hele
 * channel-pick-flow te laten gijzelen — bij timeout vallen we netjes
 * terug op `{ jwt: null }`.
 */
const DEFAULT_TIMEOUT_MS = 5_000;

export type FetchIdentityOptions = {
  /** Time-budget in ms. Default 5_000. */
  timeoutMs?: number;
  /**
   * Override voor unit-tests. Default: globale `fetch`. Tests injecteren
   * een fake fetch om network-paden deterministisch af te dekken zonder
   * de globale fetch te monkey-patchen.
   */
  fetchImpl?: typeof fetch;
  /**
   * Externe abort-signal. De caller (chat-controller) cancelt hiermee
   * de fetch wanneer de host wegval (SPA-route-change, panel-unmount).
   * Wordt gecombineerd met de interne timeout-abort.
   */
  signal?: AbortSignal;
};

/**
 * Roept de host-portal aan en retourneert het JWT als de bezoeker
 * ingelogd is. **Gooit nooit** — élke fout wordt gemapt op `{ jwt: null }`.
 *
 * @param identityUrl Volledige URL naar `umbraco/surface/chat/identity`
 *                    (of vergelijkbaar endpoint van een niet-Umbraco host).
 * @param options     Timeout + fetch-injection voor tests.
 */
export async function fetchIdentity(
  identityUrl: string,
  options: FetchIdentityOptions = {},
): Promise<IdentityResult> {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const fetchImpl = options.fetchImpl ?? fetch;

  // Caller's signal (controller-disconnect) gecombineerd met onze interne
  // timeout: één AbortController die op beide reageert. `AbortSignal.any`
  // is het idiom maar nog niet overal beschikbaar — een manual listener
  // werkt overal.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const onExternalAbort = () => controller.abort();
  options.signal?.addEventListener('abort', onExternalAbort);

  try {
    const response = await fetchImpl(identityUrl, {
      method: 'GET',
      credentials: 'include',
      // 302 naar /umbraco/login niet volgen — opaque-response valt door
      // naar het null-pad i.p.v. dat we een HTML-loginpagina als JSON
      // proberen te parsen.
      redirect: 'manual',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });

    // Opaque-redirect (`type === 'opaqueredirect'`, status 0) betekent
    // "uitgelogd" — exact wat we willen weten zonder de redirect te volgen.
    if (response.type === 'opaqueredirect' || response.status === 0) {
      return { jwt: null };
    }

    if (!response.ok) {
      // 5xx is een echt portal-probleem (deploy-issue, misconfig). 401/403
      // is normale uitgelogd-state — die mag stil blijven.
      if (response.status >= 500) {
        console.warn(
          `[chat] identity-endpoint returned ${response.status} — portal-misconfig?`,
        );
      }
      return { jwt: null };
    }

    const body = (await response.json()) as unknown;
    return { jwt: extractJwt(body) };
  } catch {
    // Élke fout — network, AbortError (timeout/external), JSON-parse —
    // valt hierheen. Niet-ingelogd-zijn is normale state, dus géén log.
    return { jwt: null };
  } finally {
    clearTimeout(timer);
    options.signal?.removeEventListener('abort', onExternalAbort);
  }
}

/**
 * Accepteert zowel PascalCase (`JWT`, oude Umbraco-default) als camelCase
 * (`jwt`, nieuwere portal-versies). Lege strings tellen als "geen JWT" —
 * voorkomt dat een server-side `string.Empty` als geldig token wordt
 * doorgegeven.
 */
function extractJwt(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const obj = body as Record<string, unknown>;
  const candidate = obj['JWT'] ?? obj['jwt'];
  if (typeof candidate !== 'string' || candidate.length === 0) return null;
  return candidate;
}
